import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from './ui/card';
import { GripVertical, Pencil } from 'lucide-react';
import { useIsReadOnly } from '../contexts/ReadOnlyContext';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { type PipelineNodeField } from '../services/pipelineNode.service';

interface SortableColumnItemProps {
  field: PipelineNodeField;
  onEditDescription: (field: PipelineNodeField) => void;
}

function SortableColumnItem({ field, onEditDescription }: SortableColumnItemProps) {
  const readOnly = useIsReadOnly();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || field.name || '', disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="p-3 group"
    >
      <div className="flex items-center gap-3">
        {!readOnly && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing flex-shrink-0"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {field.name || field.label || 'Unnamed Column'}
            </span>
            {field.type && (
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {field.type}
              </span>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${field.description ? 'text-muted-foreground' : 'text-muted-foreground/50 italic'}`}>
            {field.description || 'No description'}
          </p>
        </div>

        {!readOnly && (
          <button
            type="button"
            onClick={() => onEditDescription(field)}
            title="Edit Description"
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring flex-shrink-0"
            aria-label={`Edit description for ${field.name || field.label || 'column'}`}
          >
            <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>
    </Card>
  );
}

interface SourceColumnsReorderListProps {
  fields: PipelineNodeField[];
  onFieldsChange: (fields: PipelineNodeField[]) => void;
}

export function SourceColumnsReorderList({
  fields,
  onFieldsChange,
}: SourceColumnsReorderListProps) {
  const readOnly = useIsReadOnly();
  const [editingField, setEditingField] = useState<PipelineNodeField | null>(null);
  const [editedDescription, setEditedDescription] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (readOnly || !over || active.id === over.id) return;

    const oldIndex = fields.findIndex(
      (f) => (f.id || f.name) === active.id
    );
    const newIndex = fields.findIndex(
      (f) => (f.id || f.name) === over.id
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const newFields = arrayMove(fields, oldIndex, newIndex);
      onFieldsChange(newFields);
    }
  };

  const handleEditDescription = (field: PipelineNodeField) => {
    setEditedDescription(field.description || '');
    setEditingField(field);
  };

  const handleSaveDescription = () => {
    if (!editingField) return;
    const editingKey = editingField.id || editingField.name;
    onFieldsChange(
      fields.map((f) =>
        (f.id || f.name) === editingKey
          ? { ...f, description: editedDescription.trim() }
          : f
      )
    );
    setEditingField(null);
  };

  if (!fields || fields.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No columns defined</p>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((f) => f.id || f.name || '')}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {fields.map((field) => (
              <SortableColumnItem
                key={field.id || field.name}
                field={field}
                onEditDescription={handleEditDescription}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Dialog
        open={!!editingField}
        onOpenChange={(open) => {
          if (!open) setEditingField(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Edit Description: {editingField?.name || editingField?.label || ''}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="Describe this column"
              rows={4}
              autoFocus
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingField(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDescription}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
